import { useShallow } from 'zustand/shallow'

import type { Item } from '@/stores/CartStore'
import useCartStore from '@/stores/CartStore'
import useDialogsStore from '@/stores/DialogsStore'

// * Domain constants
export const TEMPERATURE_SENSOR_PARTS = [
  'R35825', // ReefSense Temperature probe
  'R35821', // ReefSense pH & Temp Probe
  'R35820', // ReefSense Salinity & Temp Probe
  'R35823', // ATO module with integrated digital temperature sensor
] as const

// Parts that already include a temperature sensor
const PARTS_WITH_TEMP_SENSOR = [
  'R35821', // ReefSense pH & Temp Probe
  'R35820', // ReefSense Salinity & Temp Probe
  'R35823', // ATO module with integrated digital temperature sensor
] as const

const STANDALONE_TEMP_PROBE = 'R35825' // ReefSense Temperature probe

// * Domain types
enum ConflictType {
  DUPLICATE_TEMPERATURE_SENSOR = 'duplicateTemperatureSensor',
}

type ConflictDetectionResult = {
  hasConflict: boolean
  conflictType?: ConflictType
  conflictingItem?: Item
}

// ? Helpers
const getActiveCartItemNumbers = (items: Item[], hubId: string): string[] =>
  items
    .filter(cartItem => cartItem.hubId === hubId && cartItem.amount > 0)
    .map(cartItem => cartItem.rNumber)

// ? Business rule functions
// Warning should only show when adding standalone temp probe (R35825)
// to a cart that already has pH&Temp, ATO, or Salinity&Temp
const hasDuplicateTemperatureSensor = (
  item: Item,
  cartItemNumbers: string[],
): boolean => {
  // Only show warning if incoming item is the standalone temperature probe
  if (item.rNumber !== STANDALONE_TEMP_PROBE) {
    return false
  }

  // Check if cart has any parts that already include temp sensor
  return PARTS_WITH_TEMP_SENSOR.some(part => cartItemNumbers.includes(part))
}

const detectConflict = (
  item: Item,
  cartItemNumbers: string[],
  getByRNumber: (rNumber: string) => Item | undefined,
): ConflictDetectionResult => {
  if (hasDuplicateTemperatureSensor(item, cartItemNumbers)) {
    const existingSensorRNumber = cartItemNumbers.find(rNumber =>
      TEMPERATURE_SENSOR_PARTS.includes(rNumber as typeof TEMPERATURE_SENSOR_PARTS[number]),
    )

    if (existingSensorRNumber) {
      const conflictingItem = getByRNumber(existingSensorRNumber)
      if (conflictingItem) {
        return {
          hasConflict: true,
          conflictType: ConflictType.DUPLICATE_TEMPERATURE_SENSOR,
          conflictingItem,
        }
      }
    }
  }

  return { hasConflict: false }
}

// Main hook
const useItemConflictWarning = (item: Item) => {
  const cartItems = useCartStore(useShallow(state => state.items))
  const getByRNumber = useCartStore(state => state.getByRNumber)

  const { setWarningInfo, toggle } = useDialogsStore(useShallow(state => ({
    toggle: state.toggle,
    setWarningInfo: state.setWarningInfo,
  })))

  const activeCartItemNumbers = getActiveCartItemNumbers(cartItems, item.hubId)

  const showConflictWarning = (conflict: ConflictDetectionResult): void => {
    switch (conflict.conflictType) {
      case ConflictType.DUPLICATE_TEMPERATURE_SENSOR:
        if (conflict.conflictingItem) {
          setWarningInfo('temperatureSensor', item, conflict.conflictingItem)
        }
        break
    }
    toggle('warning')
  }

  const detectAndShowConflictWarning = (): boolean => {
    const conflict = detectConflict(item, activeCartItemNumbers, getByRNumber)

    if (conflict.hasConflict) {
      showConflictWarning(conflict)
      return true
    }

    return false
  }

  return {
    checkWarningDialog: detectAndShowConflictWarning,
    // ? Only for testing
    hasConflict: () => detectConflict(item, activeCartItemNumbers, getByRNumber).hasConflict,
  }
}

export default useItemConflictWarning
